'use client';

import { useParams } from 'next/navigation';
import React from 'react';

import { doTranslation } from '@/app/i18n/client';
import Button from '@/components/global/buttons/button';

import styles from '@/components/home-page/home-page-static.module.scss';

import burlington from '@/public/assets/images/global/static/carriers/burlington.png';
import fullington from '@/public/assets/images/global/static/carriers/fullington.png';
import greyhound from '@/public/assets/images/global/static/carriers/greyhound.png';
import jefferson from '@/public/assets/images/global/static/carriers/jefferson.png';
import martzTrailways from '@/public/assets/images/global/static/carriers/martz-trailways.png';
import indianTrails from '@/public/assets/images/global/static/carriers/indian-trails.png';
import trailways from '@/public/assets/images/global/static/carriers/trailways.png';
import peterPan from '@/public/assets/images/global/static/carriers/peter-pan.png';

import rental from '@/public/assets/images/global/static/rental.svg';
import location from '@/public/assets/images/global/static/location.svg';
import tripPackage from '@/public/assets/images/global/static/package.svg';
import {default as Image} from '../global/images/resized-cdn-image';

interface Props {
  lng: string;
}

const HomePageStatic = (props: Props): React.JSX.Element => {
  const { lng }: any = useParams();
  const { t } = doTranslation(lng);

  return (
    <div className={styles.static}>
      <div className={styles['brand-trust']}>
        {process.env.NEXT_PUBLIC_COUNTRY == 'us' && (
          <div className={styles.row}>
            <div className={styles.content}>
              <h2 className={styles.title}>{t('The Brand Millions Have Trusted')}</h2>
              <p className={styles.subtitle}>
                {t(
                  `${t(
                    'Explore options from trusted providers such as'
                  )} Adirondack, Burlington, Fullington, Martz Trailways, Pine Hill, New York Trailways, Peter Pan Bus Lines, Greyhound, Jefferson Lines, Express Arrow, Indian Trails, ${t(
                    'and more. Find convenient travel solutions with our wide range of services. Start planning your journey now!'
                  )}.`
                )}
              </p>
            </div>
            <div className={styles.logos}>
              <div className={styles.row}>
                <Image
                  src={fullington}
                  alt={t('Fullington Trailways Logo')}
                  loading="lazy"
                  width={120}
                />
                <Image
                  src={martzTrailways}
                  alt={t('Martz Trailways Logo')}
                  loading="lazy"
                  width={120}
                />
              </div>
              <div className={styles.row}>
                <Image
                  src={trailways}
                  alt={t('Trailways Adirondack Pine Hill New York Logo')}
                  loading="lazy"
                  width={120}
                />
                <Image
                  src={greyhound}
                  alt={t('Greyhound Logo')}
                  loading="lazy"
                  width={120}
                />
              </div>
              <div className={styles.row}>
                <Image
                  src={burlington}
                  alt={t('Burlington Trailways Logo')}
                  loading="lazy"
                  width={120}
                />
                <Image
                  src={indianTrails}
                  alt={t('Indian Trails Since 1910 Logo')}
                  loading="lazy"
                  width={120}
                />
              </div>
              <div className={styles.row}>
                <Image
                  src={jefferson}
                  alt={t('Jefferson Lines Logo')}
                  loading="lazy"
                  width={120}
                />
                <Image
                  src={peterPan}
                  alt={t('Peter Pan Bus Lines Logo')}
                  loading="lazy"
                  width={120}
                />
              </div>
            </div>
          </div>
        )}
        <div className={styles.links}>
          <a
            className={styles.link}
            id="gtm-home_body_charter"
            href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/charter-bus-rental/`}
          >
            <Image src={rental} alt="" aria-hidden="true" loading="lazy" width={180} height={180}/>
            <span className={styles.title}>{t('Charter / Bus Rental')}</span>
          </a>
          <a
            className={styles.link}
            id="gtm-home_body_wheres_my_bus"
            href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/wheres-my-bus/`}
          >
            <Image src={location} alt="" aria-hidden="true" loading="lazy" width={180} height={180}/>
            <span className={styles.title}>{t("Where's My Bus?")}</span>
          </a>
          <a
            className={styles.link}
            id="gtm-home_body_trip_packages"
            href={`${process.env.NEXT_PUBLIC_TRAILWAYS_SITE_BASE}/packages/`}
          >
            <Image src={tripPackage} alt="" aria-hidden="true" loading="lazy" width={180} height={180}/>
            <span className={styles.title}>{t('Trip Packages')}</span>
          </a>
        </div>
      </div>
      <div className={styles.contents}>
        <div>
          <p className={styles.text}>
            {t(
              'Trailways is an affordable option for local and long-distance travel. Whether you are visiting family a few hours away, going on a weekend road trip with friends, or planning a long-distance trip, Trailways is the most cost-effective way to get there.'
            )}
          </p>
          <span className={styles.space}></span>
          <p className={styles.text}>
            {t(
              'Instead of driving or facing delay after delay on a discount airline, you can book a comfortable seat on a clean, reliable bus. Trailways is North America’s premier private bussing company for a reason. Avoid the hassle of air travel and see some of the most beautiful scenery in the world right outside your window. Buy a bus ticket on Trailways today! Coming soon, train tickets too.'
            )}
          </p>
        </div>
        <div className={styles.content}>
          <h1 className={styles.title}>{t('Why Choose Trailways for Your Bus Trip?')}</h1>
          <p className={styles.text}>
            {t(
              "Trailways has numerous options, including for local or long-distance travel. Whether you’re planning a fun day of shopping with friends or you’re taking your family out for an adventure, Trailways' express routes can deliver you to your destination and take you back when you’re done — all without the stress of driving."
            )}
          </p>
          <span className={styles.space}></span>
          <p className={styles.text}>
            <span>
              {t(
                'Booking on our website is super easy. We show you all of the best options for travel in one place. We offer the lowest prices on bus tickets by Trailways and all of our ground transportation partners, such as Peter Pan, Greyhound, FlixBus, and Megabus.'
              )}
            </span>
            <span>
              {t(
                'You can buy however many tickets you want at any time. If you prefer, you can also call our customer care center for assistance with booking by phone. If you aren’t sure when you want to return, just book one way. You can be confident that we will have multiple return trips available. It’s the perfect way to travel in North America.'
              )}
            </span>
          </p>
          <span className={styles.space}></span>
          <p className={styles.text}>
            {t(
              'We can also accommodate cross-country or even international trips. Our extensive network of buses and partner carriers ensures smooth connections in every city. Saver Fare ensures that your bus trip will be as affordable as it is convenient and fun!'
            )}
          </p>
        </div>
        <div className={styles.content}>
          <h1 className={styles.title}>{t('Where Can I Travel on a Trailways Bus?')}</h1>
          <p className={styles.text}>
            {t(
              'Trailways and its partners have bus terminals in every state in the Continental United States (not Alaska and Hawaii) and in the District of Columbia. We also partner with other carriers to offer services to many popular Canadian and Mexican destinations.'
            )}
          </p>
          <span className={styles.space}></span>
          <p className={styles.text}>
            {t(
              'If you do not see a Trailways bus route that takes you to where you want to go, talk to one of our booking agents. They can help you tailor your route to be affordable and efficient. Unlike the airlines, Trailways is not limited by the need to only move between airports. Our routes have several stops, so you can get out, stretch your legs, and get a meal or a cup of coffee.'
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

export default HomePageStatic;
